import { AttendanceDataWithLocation, AttendanceList, dataGeneralFooter, DataListMarkings } from '../interfaces/attendance.interface';
import { getTextWithPic } from 'src/app/shared/helpers/textWithPic';
import {
  getArrayWithBadgeAndIcon,
  getSimpleBadge,
  getStateBadge,
} from 'src/app/shared/helpers/badgesBuilder';
import { NameWithPic } from 'src/app/shared/interfaces/textWithPic.interface';
import { setHoursMins, setMinToHours } from 'src/app/shared/helpers/timeAndHours';
import { getIcon } from 'src/app/shared/helpers/iconBuilder';
import { AccessService } from 'src/app/core/services/access.service';
import * as moment from 'moment';
import { GenericResponseList } from 'src/app/shared/interfaces/table.interface';
import { GenericResponse } from 'src/app/shared/interfaces/generic.interface';
import { TIME_MARKS } from 'src/app/shared/components/marking/helpers/marks-enums';

const normalizeState = (state: string): string => {
  if (state === 'FUERA-DE-HORARIO') return 'FUERA DE HORARIO';
  if (state === 'PERSONAL CONFIANZA') return 'P. CONFIANZA';
  return state;
};

const buildNameWithPic = (e: any): NameWithPic => ({
  urlPic: e.sUrl_Foto,
  primaryText: { text: e.sNombre_Colaborador },
  secundaryText: {
    text: e.sDocumento_Identidad,
    action: 'send-to-whatsapp',
    iconHover: { name: 'whatsapp_filled', class: 'text-mg-green-dark', type: 'mine' },
  },
});

const getCombinateHours = (fecha: string, ingreso: string, salida: string): string => {
  let horas = '';
  if (ingreso && salida) horas = `${ingreso} - ${salida}`;
  else if (ingreso && !salida) horas = `${ingreso} -`;
  else if (!ingreso && salida) horas = salida;
  return horas ? `${fecha}\n${horas}` : fecha;
};

const getInletBadge = (combinatehours: string, stripesEntrada: boolean) => {
  if (!combinatehours) return getSimpleBadge('-');
  return getSimpleBadge(combinatehours, undefined, '');
};

const getOutletBadge = (hora_Salida: string, stripesSalida: boolean) => {
  if (!hora_Salida) return getSimpleBadge('-');
  const splitTime = hora_Salida.split('.')[0];
  return stripesSalida
    ? getSimpleBadge(splitTime, '', 'stripes')
    : getSimpleBadge(splitTime);
};

const buildTotalHoursBadge = (e: any) => {
  let badge = getSimpleBadge(e.nMinutos_Planilla ? setHoursMins(e.nMinutos_Planilla) : '-', '', '', '!max-w-[8rem]');
  if (e.nMinutos_Jornada != null && e.hora_Salida) {
    const minJornada = e.nMinutos_Jornada;
    const tooltip = e.dCantidad_Tardanza ? `Se descontó ${setMinToHours(e.dCantidad_Tardanza)} de tardanza en el cálculo total de horas trabajadas` : '';
    const minJornadaHorario = e.nMinutos_Almuerzo_horario ? e.nMinutos_Jornada_horario - e.nMinutos_Almuerzo_horario - e.nMinutos_Permiso : e.nMinutos_Jornada_horario - e.nMinutos_Permiso;

    let colorBabge = { color: 'main-blue', tooltip: 'Puntual' };
    if (minJornada > minJornadaHorario) {
      colorBabge = { color: 'yellow', tooltip: `Excede ${setMinToHours(minJornada - minJornadaHorario)} ${tooltip}` };
    } else if (minJornada < minJornadaHorario) {
      colorBabge = { color: 'red', tooltip: `Faltan ${setMinToHours(minJornadaHorario - minJornada)} ${tooltip}` };
    }
    badge = getSimpleBadge(setMinToHours(minJornada), colorBabge.tooltip, colorBabge.color, '!max-w-[8rem]');
  }
  return badge;
};

const extractIcMarkList = (marks: string) => {
  if (marks) {
    const marksArray = JSON.parse(marks);
    if (Array.isArray(marksArray) && marksArray.length > 0) {
      return { ...getIcon({ name: marksArray[0].sicono, type: 'material' }, '', 'main', true), action: 'assistance-list-markings' };
    }
  }
  return { ...getIcon({ name: '', type: 'material' }, '', 'transparent', false), action: null };
};

const buildActionMenuCards = (visibleMenuActions: boolean) => [
  { label: 'Enviar mensaje', icon: { name: 'whatsapp_filled', type: 'mine', class: 'text-mg-green-dark text-lg' }, type: 'submit', action: 'send-to-whatsapp', visible: visibleMenuActions },
  { label: 'Editar estado de asistencia', icon: { name: 'edit', type: 'material', class: 'text-mg-main-blue' }, type: 'submit', action: 'edit-state-assistance', visible: false },
  { label: 'Actualizar estado', icon: { name: 'update', type: 'material', class: 'text-mg-green' }, type: 'submit', action: 'update-state-assistance', visible: visibleMenuActions },
];

const processAttendanceRow = (e: any, visibleMenuActions: boolean) => {
  e.sEstado = normalizeState(e.sEstado);
  e.NameWithPic = getTextWithPic(buildNameWithPic(e));

  const fecha = moment(e.dFecha_Registro_Asistencia).format('DD MMM YYYY');
  const ingreso = e.hora_Ingreso ? e.hora_Ingreso.split('.')[0] : '';
  const salida = e.hora_Salida ? e.hora_Salida.split('.')[0] : '';
  const combinatehours = getCombinateHours(fecha, ingreso, salida);

  const stripesEntrada = e.nEstado_Solicitud_Marca_Entrada == 1;
  const stripesSalida = e.nEstado_Solicitud_Marca_Salida == 1;

  e.badgeInletMark = getInletBadge(combinatehours, stripesEntrada);

  const outletBadge = getOutletBadge(e.hora_Salida, stripesSalida);
  e.badgeIntletLunchMark = outletBadge;
  e.badgeOutletLunchMark = outletBadge;
  e.badgeOutletMark = outletBadge;

  e.badgeTotalHours = buildTotalHoursBadge(e);
  e.icMarkList = extractIcMarkList(e.marks);
  e.badgeStates = getStateBadge(e.sEstado, e.sEstado);
  e.amountOfDelay = getSimpleBadge(setMinToHours(e.dCantidad_Tardanza));
  e.badgeWorkingDay = getStateBadge(e.sTipo_Modalidad, e.sTipo_Modalidad);

  e.icPermission = e.bPermiso ? getIcon({ name: 'receipt_long', type: 'material' }, 'Solicitud', '', true) : false;
  e.icJustify = validateIfJustificationsExist([e.bJustificacion!]) ? getIcon({ name: 'chat_Bubble', type: 'material' }, 'Motivo', 'orange', true) : false;
  e.icHistoric = e.flagHistorico ? getIcon({ name: 'history', type: 'material' }, 'Historial de cambios', '', true) : false;

  const statusLabel = e.sEstado == 'EN TOLERANCIA' ? 'TARDANZA' : (e.sEstado == 'PERSONAL CONFIANZA' ? 'P. CONFIANZA' : e.sEstado);
  const statusColor = colorBadgeStatus(e.sEstado == 'PERSONAL CONFIANZA' ? 'P. CONFIANZA' : e.sEstado);

  e.badgeStatesWithIcon = getArrayWithBadgeAndIcon(
    { label: statusLabel, tooltip: '', color: statusColor, aditionalCss: 'border' },
    e.bPermiso ? { icon: { name: 'receipt_long', type: 'material' }, tooltip: 'Solicitud', action: 'assistance-request-detail', typeAction: 'modal', color: 'main' } : null
  );

  e.badgeAmountOfDelayWithIcon = getArrayWithBadgeAndIcon(
    { label: setMinToHours(e.dCantidad_Tardanza), tooltip: '', color: 'main' },
    e.sJustificacion ? { icon: { name: 'chat_bubble', type: 'material' }, tooltip: 'Motivo', action: 'assistance-mark-justify', typeAction: 'modal', color: 'main' } : null
  );

  e.badgeMarksListWithIcon = e.nEstado !== 11
    ? functiongetBagdeLabelAndIconForMarksListOrMarkCreate(e.hora_Ingreso, stripesEntrada, e.nId_User)
    : false;

  const iconMark = (typeof e.icMarkList !== 'boolean' && e.icMarkList?.icon) ? e.icMarkList.icon : { name: 'alarm', type: 'material' };

  e.badgeDateAssistanceWithIcon = getArrayWithBadgeAndIcon(
    { label: moment(e.dFecha_Registro_Asistencia).format('DD MMM YYYY'), tooltip: '', color: 'main' },
    { icon: iconMark, tooltip: 'Ver marcaciones', action: 'assistance-list-markings', typeAction: 'submit', color: 'main' }
  );

  e.actionMenuCards = buildActionMenuCards(visibleMenuActions);

  return { tardanza: e.dCantidad_Tardanza, isFalta: e.sEstado == 'FALTA', isPermiso: e.bPermiso };
};

export function transforDataAttendaceList(
  response: GenericResponseList<AttendanceList>,
  getInputs: string,
  _accessService: AccessService
) {
  if (response.data == null) response.data = [];
  let tardiesSum = 0;
  let faultsSum = 0;
  let requestSum = 0;
  let visibleMenuActions: boolean = getPermissionAndIndividualList(getInputs, _accessService);

  for (const e of response.data) {
    const sums = processAttendanceRow(e, visibleMenuActions);
    tardiesSum += sums.tardanza;
    if (sums.isFalta) faultsSum += 1;
    if (sums.isPermiso) requestSum += 1;
  }

  response.footer = {
    numberOfFaults: faultsSum == 1 ? faultsSum + ' falta' : faultsSum + ' faltas',
    numberOfTardies: setMinToHours(tardiesSum),
    numberOfRequest: requestSum == 1 ? requestSum + ' solicitud' : requestSum + ' solicitudes',
  };
  return response;
}

function colorBadgeStatus(state: string) {
  switch (state) {
    case 'SIN MARCA': return 'gray';
    case 'PUNTUAL': return 'green';
    case 'TARDANZA':
    case 'EN TOLERANCIA': return 'orange';
    case 'FUERA DE HORARIO': return 'coral';
    case 'FALTA': return 'red';
    case 'PERMISO': return 'teal';
    case 'VACACIONES': return 'purple';
    case 'FERIADO': return 'pink';
    case 'PERSONAL CONFIANZA':
    case 'P. CONFIANZA': return 'green';
    default: return 'main';
  }
}

function functiongetBagdeLabelAndIconForMarksListOrMarkCreate(
  entryMark: string | null,
  extemporaneousMarkEntry: boolean,
  nId_User: number,
  estado?: string,
  icMarkList?: any | null
) {
  if (entryMark && estado != 'FALTA') {
    return getArrayWithBadgeAndIcon(
      {
        label: entryMark.includes(':') ? entryMark.split('.')[0] : moment(entryMark).format('DD MMM YYYY'),
        tooltip: '',
        color: extemporaneousMarkEntry ? 'stripes' : 'main',
      },
      {
        icon: {
          name: icMarkList && icMarkList.icon ? icMarkList.icon.name : 'alarm',
          type: icMarkList && icMarkList.icon ? icMarkList.icon.type : 'material',
        },
        tooltip: 'Ver marcaciones',
        action: 'assistance-list-markings',
        color: 'main',
        typeAction: 'modal',
      }
    );
  }

  return getArrayWithBadgeAndIcon(
    { label: '-', tooltip: '', color: 'main' },
    { icon: { name: 'alarm', type: 'material' }, tooltip: 'Ver marcaciones', action: 'assistance-list-markings', color: 'main', typeAction: 'modal' }
  );
}

function getPermissionAndIndividualList(getInputs: string, _accessService: AccessService) {
  let typeList = getInputs.split('&');
  let bool = 'false';
  for (const e of typeList) {
    if (e.includes('bListado_individual')) {
      bool = e.split('=')[1];
    }
  }
  const hasUpdatePermission = _accessService.validarPermisoSlug('ATTENDANCE-UPDATE');
  return hasUpdatePermission && !JSON.parse(bool);
}

export function setDataGeneralFooterAttendaceList(response: GenericResponse<dataGeneralFooter>) {
  if (response.isSuccess && response.data != null) {
    response.data.numberOfFaults = response.data.totalFalta == 1 ? response.data.totalFalta + ' falta' : response.data.totalFalta + ' faltas';
    response.data.numberOfTardies = setMinToHours(response.data.cantidadTardanza);
    response.data.numberOfRequest = response.data.totalSolicitud == 1 ? response.data.totalSolicitud + ' solicitud' : response.data.totalSolicitud + ' solicitudes';
  }
  return response;
}

export function setDataForJustifications(response: any) {
  if (response.isSuccess && response.data != null) {
    response.data = response.data.map((e: any) => {
      return {
        nId_Historico: e.nId_Historico,
        sEsExtemporanea: e.sEsExtemporanea,
        dFecha_Creador: e.sFecha_Creador,
        sCambios: `<strong>${e.sJustificacion}</strong>`,
        sTipoMarcacion: e.sTipoMarcacion,
        marcas: [],
      }
    });
  }
  return response;
}

const validateIfJustificationsExist = (justificationTypes: boolean[]): boolean => {
  return justificationTypes.some((val) => val);
}

export const formatMarksData = (data: DataListMarkings[]): AttendanceDataWithLocation[] => {
  return data.map((element: DataListMarkings) => {
    const { longitude, latitude, ...rest } = element;

    return {
      ...rest,
      location: (element.longitude && element.latitude) ? {
        longitude: element.longitude,
        latitude: element.latitude,
        title: getTitle(element.dFecha_Marca, element.nTipo_Marcacion),
        fillColor: getColor(element.nTipo_Marcacion),
        nTipo_Marcacion: element.nTipo_Marcacion,
        nId_Marca: element.nId_Marca,
      } : null,
    }
  });
}

const getTitle = (dFecha_Marca: string, nTipo_Marcacion: number): string => {
  let markTipe: string = "";
  switch (nTipo_Marcacion) {
    case TIME_MARKS.ENTRADA: markTipe = "Entrada: "; break;
    case TIME_MARKS.SALIDA_REFRIGERIO: markTipe = "Salida Refrigerio: "; break;
    case TIME_MARKS.RETORNO_REFRIGERIO: markTipe = "Retorno Refrigerio: "; break;
    case TIME_MARKS.SALIDA: markTipe = "Salida: "; break;
    default: markTipe = ""; break;
  }
  markTipe += dFecha_Marca;
  return markTipe;
}

const getColor = (nTipo_Marcacion: number): string => {
  let color: string = "#FF0000";
  switch (nTipo_Marcacion) {
    case TIME_MARKS.ENTRADA: color = "#3cbb70"; break;
    case TIME_MARKS.SALIDA_REFRIGERIO: color = "#e5a122"; break;
    case TIME_MARKS.RETORNO_REFRIGERIO: color = "#f38360"; break;
    case TIME_MARKS.SALIDA: color = "#46d7d1"; break;
    default: color = "#FF0000"; break;
  }
  return color;
}

const formatTime = (time: string): string => {
  return time.slice(0, 8);
}